import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextInput,
  Checkbox,
  //DatePicker,
  //DatePickerInput,
  Tag,
  Tooltip,
  Button,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";


const ResaleDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 3,
  lastSavedStep = 0,
  onStepChange,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {

  // ------------------------------ DEBUG INFO
  console.log('🔍 PropertyDetails (Step 3) Debug:', {
    receivedFormMode: formMode,
    receivedCurrentStep: currentStep, 
    receivedLastSavedStep: lastSavedStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    shouldBeEdit: lastSavedStep >= currentStep,
    calculation: `lastSavedStep(${lastSavedStep}) >= currentStep(${currentStep}) = ${lastSavedStep >= currentStep}`
  });

  // ------------------------------ CHILD EDIT MODE
  const isEditMode = useMemo(() => {
    const result = formMode === "edit" || lastSavedStep >= currentStep;
    console.log('🔄 Calculating isEditMode:', { 
      formMode, 
      lastSavedStep, 
      currentStep, 
      result 
    });
    return result;
  }, [formMode, lastSavedStep, currentStep]);

  // ------------------------------ STATE
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [newTag, setNewTag] = useState("");
  const [customTags, setCustomTags] = useState([]);

  // Form values
  const [form, setForm] = useState({
    // Pricing Information
    expectedPrice: "",
    priceNegotiable: false,
    
    // Ownership Details
    ownershipType: "",
    
    // Availability
    availableFrom: "",
    
    // Ideal For Tags
    idealFor: []
  });

  // ------------------------------ MAPPING
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      expectedPrice: "com_sale_expected_price",
      priceNegotiable: "com_sale_price_negotiable",
      ownershipType: "resale_ownership_type",
      availableFrom: "com_sale_available_from",
      idealFor: "com_sale_ideal_for",
    }),
    []
  );

  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );
  
  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);
      return attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

  // Get predefined tags from API for "Ideal For" section
  const predefinedTags = useMemo(() => {
    return getOptions("idealFor").map(item => ({
      id: item.id,
      label: item.attr_val_name || item.label || item.value,
    }));
  }, [getOptions]);

  // ------------------------------ VALIDATION
  const validationRules = useMemo(
    () => ({
      expectedPrice: { required: true,   
                      message: "expected price is Required"

      },
      ownershipType: { required: true ,
                      message:"Ownership Type is Required"
      },
    
    }),
    []
  );
const getFieldError = useCallback(
  (field) => {
    const rule = validationRules[field];
    if (!rule) return null;

    const val = form[field];

    if (rule.required) {
      const isEmpty =
        val === undefined ||
        val === null ||
        val === "" ||
        (Array.isArray(val) && val.length === 0);

      if (isEmpty) {
        return rule.message || "This field is required";
      }
    }

    return null;
  },
  [form, validationRules]
);

  const handleBlur = useCallback((f) => setTouched((p) => ({ ...p, [f]: true })), []);

  // Update field
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // ========== DATE PICKER HANDLING ==========
  const handleDateChange = useCallback((selectedDates) => {
    if (selectedDates && selectedDates.length > 0) {
      const selectedDate = selectedDates[0];
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      updateField("availableFrom", formattedDate);
    }
  }, [updateField]);

const handleDateInputChange = useCallback((event) => {
  let value = event.target.value;
  value = value.replace(/[^0-9/]/g, "");
  if (value.length > 10) return;

  updateField("availableFrom", value);
}, [updateField]);

  // ========== TAG MANAGEMENT ==========
  const handleTagToggle = useCallback((tagId) => {
    setForm(prev => {
      const currentTags = prev.idealFor || [];
      if (currentTags.includes(tagId)) {
        return {
          ...prev,
          idealFor: currentTags.filter(id => id !== tagId)
        };
      } else {
        return {
          ...prev,
          idealFor: [...currentTags, tagId]
        };
      }
    });
  }, []);

  const handleAddNewTag = useCallback(() => {
    if (newTag.trim()) {
      const trimmedTag = newTag.trim();
      
      const tagExists = [...predefinedTags, ...customTags].some(
        tag => tag.label.toLowerCase() === trimmedTag.toLowerCase()
      );
      
      if (!tagExists) {
        const newTagId = `custom_${Date.now()}`;
        const customTag = {
          id: newTagId,
          label: trimmedTag
        };
        
        setCustomTags(prev => [...prev, customTag]);
        setForm(prev => ({
          ...prev,
          idealFor: [...prev.idealFor, newTagId]
        }));
        setNewTag("");
      } else {
        setError(`Tag "${trimmedTag}" already exists`);
      }
    }
  }, [newTag, predefinedTags, customTags]);

  const isTagSelected = useCallback((tagId) => {
    return form.idealFor.includes(tagId);
  }, [form.idealFor]);

  // HYDRATE FORM WITH EXISTING DATA
useEffect(() => {
  if (!data || !attributeValues.length) return;

  console.log("💧 Hydrating form with data:", data);

  const attributes = data.attributes || {};

  const updated = {
    expectedPrice: "",
    priceNegotiable: false,
    ownershipType: "",
    availableFrom: "",
    idealFor: [],
  };

  const newCustomTags = [];

  Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(
    ([fieldKey, apiKey]) => {

      const value = attributes[apiKey];

      if (value === undefined || value === null) return;

      // Checkbox
      if (fieldKey === "priceNegotiable") {

        updated[fieldKey] =
          value === "Yes" || value === true;

      }

      // Normal fields
      else if (
        fieldKey === "expectedPrice" ||
        fieldKey === "ownershipType" ||
        fieldKey === "availableFrom"
      ) {

        updated[fieldKey] = value;

      }

      // Ideal For tags
      else if (
        fieldKey === "idealFor" &&
        Array.isArray(value)
      ) {

        const mappedTagIds = [];

        value.forEach((tagLabel) => {

          const normalizedTag = String(tagLabel)
            .trim()
            .toLowerCase();

          const predefinedTag = predefinedTags.find(
            (tag) =>
              tag.label.trim().toLowerCase() === normalizedTag
          );

          if (predefinedTag) {

            mappedTagIds.push(predefinedTag.id);

          } else {

            const customTagId =
              `custom_${normalizedTag}`;

            newCustomTags.push({
              id: customTagId,
              label: tagLabel,
            });

            mappedTagIds.push(customTagId);
          }
        });

        updated[fieldKey] = mappedTagIds;
      }
    }
  );

  setCustomTags(newCustomTags);

  console.log("💧 Final hydrated form:", updated);

  setForm(updated);

}, [
  data,
  attributeValues,
  FIELD_TO_ATTRIBUTE_MAP,
  predefinedTags,
]);

  // ------------------------------ SAVE
  const handleSave = useCallback(
    async (navigateNext = false) => {
      console.log('💾 Starting save process...', {
        navigateNext,
        propertyUuid,
        currentStep,
        lastSavedStep,
        isEditMode
      });

      setTouched(
        Object.keys(form).reduce((a, k) => ({ ...a, [k]: true }), {})
      );

      const invalid = Object.keys(validationRules).some((k) => !!getFieldError(k));
      if (invalid) {
        setError("Please fill all required fields");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        let uuid = propertyUuid;

        if (!uuid && createProperty) {
          console.log('🆕 Creating new property...');
          uuid = await createProperty();
          console.log('✅ Property created with UUID:', uuid);
          onSaveSuccess?.(uuid);
        } else {
          console.log('📝 Updating existing property:', uuid);
        }

        const attributes = {};
        Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([key, apiKey]) => {
          const val = form[key];
          if (val !== "" && val !== null && val !== undefined) {
            if (key === "priceNegotiable") {
              attributes[apiKey] = val ? "Yes" : "No";
            } else if (key === "idealFor" && Array.isArray(val)) {
              const tagLabels = val.map(tagId => {
                const predefinedTag = predefinedTags.find(tag => tag.id === tagId);
                if (predefinedTag) return predefinedTag.label;
                const customTag = customTags.find(tag => tag.id === tagId);
                if (customTag) return customTag.label;
                return tagId;
              }).filter(Boolean);
              
              if (tagLabels.length > 0) {
                attributes[apiKey] = tagLabels;
              }
            } else {
              attributes[apiKey] = isNaN(val) ? val : Number(val);
            }
          }
        });

        console.log('💾 Saving attributes:', attributes);
        await saveSectionAttributes(uuid, attributes, form, "resale_details");

        setSuccess("Resale details saved successfully!");
        console.log('✅ Save completed, calling onComplete...');
        onComplete?.(form);

        if (navigateNext) {
          console.log('➡️ Navigating to next step...');
          onSaved?.(uuid, form);
          onNavigateNext?.(form);
        }
      } catch (err) {
        console.error('❌ Save failed:', err);
        setError("Failed to save property details");
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      validationRules,
      getFieldError,
      propertyUuid,
      createProperty,
      onSaveSuccess,
      saveSectionAttributes,
      onComplete,
      onSaved,
      onNavigateNext,
      FIELD_TO_ATTRIBUTE_MAP,
      predefinedTags,
      customTags,
      currentStep,
      lastSavedStep,
      isEditMode,
    ]
  );

  // ------------------------------ RENDER UI
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem" , backgroundColor:"white"}}>
              <Stack gap={6}>
                
                {/* PRICING INFORMATION */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderNumberField("expectedPrice", "Expected Price ", "Enter amount", true)}
                      <div style={{ marginTop: "0.5rem" }}>
                        <Checkbox
                          id="priceNegotiable"
                          labelText="Price Negotiable"
                          checked={form.priceNegotiable}
                          onChange={(e) => updateField("priceNegotiable", e.target.checked)}
                        />
                      </div>
                    </Column>
                    <Column lg={8}>
                      {renderDropdown("ownershipType", "Ownership Type")}
                    </Column>
                  </Grid>
                </FormGroup>

                
{/* AVAILABLE FROM */}
<FormGroup>
  <Grid>
    <Column lg={8}>
      <TextInput
        id="availableFrom"
        type="date"
        labelText="Available From"
        value={form.availableFrom || ""}
        onChange={(e) => updateField("availableFrom", e.target.value)}
        onBlur={() => handleBlur("availableFrom")}
      />
    </Column>
  </Grid>
</FormGroup>

                {/* IDEAL FOR TAGS */}
                <FormGroup>
                  <Grid>
                    <Column lg={5}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
                        <h4 style={{ margin: 0, fontWeight: "600", color: "#161616" }}>Ideal For</h4>
                        <Tooltip
                          align="top"
                          label="Choose what this property is suited for by creating tags"
                        >
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Information"
                            style={{ marginLeft: "0.5rem", minWidth: "auto", padding: "0.25rem" }}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 14.5c3.6 0 6.5-2.9 6.5-6.5S11.6 1.5 8 1.5 1.5 4.4 1.5 8 4.4 14.5 8 14.5zM8 16c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"/>
                              <path d="M7.3 11.7h1.4V7.3H7.3v4.4zM8 6.2c.4 0 .7-.3.7-.7s-.3-.7-.7-.7-.7.3-.7.7.3.7.7.7z"/>
                            </svg>
                          </Button>
                        </Tooltip>
                      </div>

                      {/* All Tags - Predefined + Custom */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
                        {predefinedTags.map((tag) => (
                          <Tag
                            key={tag.id}
                            type={isTagSelected(tag.id) ? "blue" : "gray"}
                            onClick={() => handleTagToggle(tag.id)}
                            style={{
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              padding: "0.25rem 0.5rem",
                              border: isTagSelected(tag.id) ? "2px solid #0f62fe" : "2px solid transparent",
                            }}
                          >
                            {tag.label}
                          </Tag>
                        ))}
                        
                        {customTags.map((tag) => (
                          <Tag
                            key={tag.id}
                            type={isTagSelected(tag.id) ? "blue" : "gray"}
                            onClick={() => handleTagToggle(tag.id)}
                            style={{
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              padding: "0.25rem 0.5rem",
                              border: isTagSelected(tag.id) ? "2px solid #0f62fe" : "2px solid transparent",
                            }}
                          >
                            {tag.label}
                          </Tag>
                        ))}
                      </div>

                      {/* Add Custom Tag */}
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <TextInput
                          id="newTag"
                          labelText=""
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder="Add Other Tags (Create New Tag)"
                          size="sm"
                          style={{ flex: 1 }}
                        />
                        <Button
                          kind="secondary"
                          size="sm"
                          onClick={handleAddNewTag}
                          disabled={!newTag.trim()}
                          style={{ minWidth: "auto", whiteSpace: "nowrap" }}
                        >
                          Add Tag
                        </Button>
                      </div>

                      {/* Selected Tags Display */}
                      {form.idealFor.length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                          <p style={{ fontSize: "0.875rem", color: "#525252", marginBottom: "0.5rem" }}>
                            Selected: {form.idealFor.length} tag(s)
                          </p>
                        </div>
                      )}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* BUTTONS */}
                {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}   // SAVE ONLY
                    onNext={() => handleSave(true)}     // NEXT = SAVE + NAVIGATE
                    showBack
                    showSave
                    showNext
                    isSaveLoading={saving}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default ResaleDetails;